import { AfterViewInit, Component, Input, OnDestroy, OnInit} from '@angular/core';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import get from 'lodash-es/get';

import { Location, BlockoutSection } from '@finder/features/calendars/calendars.interface';
import { CalendarsService } from '@finder/features/calendars/services/calendars.service';
import { ViewportDetectionService } from '@finder/shared/services/viewport-detection/viewport-detection.service';
import { ParkSectionComponent } from '../park-section/park-section.component';
import { SectionComponent } from '@finder/shared/components/section/section.component';
import { CommonModule } from '@angular/common';
import { DisclaimerComponent } from '@finder/shared/components/disclaimer/disclaimer.component';
import { LoadingSpinnerComponent } from '@finder/shared/components/loading-spinner/loading-spinner.component';

@Component({
    selector: 'finder-calendar-park',
    templateUrl: './park-card.component.html',
    styleUrls: ['./park-card.component.scss'],
    standalone: true,
    imports: [
        ParkSectionComponent,
        SectionComponent,
        CommonModule,
        DisclaimerComponent,
        LoadingSpinnerComponent,
    ]
})
export class ParkCardComponent implements OnInit, AfterViewInit, OnDestroy {

    @Input() location: Location;
    @Input() labels: any;
    @Input() date: string;
    @Input() icons: any;
    @Input() isClosedFacilitiesExpanded = false;

    blockouts: BlockoutSection;
    events: any;
    icon: string;
    isDesktop = false;
    loading = false;
    changeDirection = true;
    parkHours: any;
    permanentSchedule: any;
    sections: Array<any>;
    emptyHoursText: string;
    closedFacilities: Array<any>;

    private destroy$ = new Subject<void>();

    constructor(
        private calendarsService: CalendarsService,
        private viewportService: ViewportDetectionService,
    ) { }

    ngOnInit() {
        // Get the empty hours message based on the section type.
        this.emptyHoursText = get(this.labels, `emptyHours.blockouts`, this.labels.emptyHours.default);

        this.parkHours = this?.location?.schedules;
        this.permanentSchedule = this.location?.permanentSchedule;
        this.icon = get(this.icons, this.location.urlFriendlyId);

        this.setSections();
        this.setBlockouts();
        this.setClosedFacilities();
        // Events are always in the first position.
        this.events = this.sections[0];
        this.isDesktop = this.viewportService.isDesktop();
        this.calendarsService.getLoadingState()
            .pipe(takeUntil(this.destroy$))
            .subscribe(state => this.loading = state);
        this.changeDirection = this.location.changeDirection && this.sections.length > 3;
    }

    ngAfterViewInit() {
        this.viewportService.onViewportResize()
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.isDesktop = this.viewportService.isDesktop();
            });
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    setClosedFacilities() {
        const closedFacilities = this?.location?.closedFacilities;
        if (!closedFacilities) {
            return;
        }

        this.closedFacilities = closedFacilities.reduce((result, facility) => {
            if (facility.refurbishment.includes(this.date)) {
                result.push(facility);
            }

            return result;
        }, []);
    }

    setSections() {
        const eventGroups = this?.location?.events ?? [];
        this.sections = eventGroups.map((group) => {
            const type = group.type;
            const { limit, viewAll, title, permanentActivity } = group;
            const events = group?.data ?? [];
            const id = `${this.location.urlFriendlyId}-${type}`;

            return {
                id,
                type,
                style: group.style,
                data: {
                    data: events,
                    limit,
                    title,
                    viewAll,
                    permanentActivity
                },
                labels: {
                    sectionTitle: title
                }
            };
        });
    }

    setBlockouts() {
        const blockouts = this.location.blockouts;
        if (!blockouts) {
            return;
        }

        this.blockouts = {
            id: `${this.location.urlFriendlyId}--blockouts`,
            data: blockouts,
            labels: {
                sectionTitle: 'finder.calendar.blockouts'
            }
        };
    }

}
